import React from "react";
import Nav from "../../component/Nav";
import {
  IoIosCash,
  IoMdAdd,
  IoIosArrowForward,
  IoIosSearch,
  IoIosClose,
} from "react-icons/io";
import Fab from "../../component/FAB";
import { useRealCash, useCashTotal, useWalletAmount } from "../../api";
import { FormatNumber } from "../../helpers";
import CashLoader from "../../loader/CashLoader";
// Reuse wallet design language — same visual primitives (hero, cards, search).
import "../wallet/wallet.css";

export default function Cash(props) {
  const { totalCash, loading } = useCashTotal();
  const { cash, isLoading } = useRealCash();
  const { walletAmounts } = useWalletAmount();
  const [search, setSearch] = React.useState("");

  // Reconciliation snapshot: catatan (wallet sum) vs kas riil (totalCash)
  const walletTotal = React.useMemo(
    () =>
      walletAmounts
        .filter((w) => !w.additional)
        .reduce((acc, w) => acc + (w.sum || 0), 0),
    [walletAmounts]
  );
  const delta = Math.round(walletTotal - (totalCash || 0));
  const reconciled = delta === 0 && (totalCash > 0 || walletTotal > 0);
  const hasReconContext =
    (totalCash || 0) > 0 || walletAmounts.length > 0;

  const filtered = React.useMemo(() => {
    if (!cash) return [];
    const q = search.trim().toLowerCase();
    if (!q) return cash;
    return cash.filter((c) => (c.name || "").toLowerCase().includes(q));
  }, [cash, search]);

  const addCash = () => props.history.push("/cash/add");
  const editCash = (d) =>
    props.history.push(`/cash/edit/${d.id}/${d.name}/${d.amount}`);

  if (loading || isLoading) {
    return (
      <>
        <Nav title="Uang Tunai" icon={IoIosCash} />
        <div className="main-padding">
          <CashLoader />
        </div>
      </>
    );
  }

  return (
    <>
      <Nav title="Uang Tunai" icon={IoIosCash} />
      <div className="main-padding mb-5">
        {/* ── Hero: total kas riil + reconciliation pill ─────── */}
        <section className="wallet-hero">
          <div className="wallet-hero__top">
            <div className="wallet-hero__label">Total Uang Tunai</div>
            {hasReconContext && (
              <span
                className={`cash-pill ${
                  reconciled ? "cash-pill--match" : "cash-pill--diff"
                }`}
                title={
                  reconciled
                    ? "Cocok dengan saldo catatan"
                    : "Beda dengan saldo catatan — periksa di Home"
                }
              >
                {reconciled
                  ? "Cocok"
                  : `Δ ${delta > 0 ? "+" : "−"}Rp ${FormatNumber(
                      Math.abs(delta)
                    )}`}
              </span>
            )}
          </div>
          <div
            className={`wallet-hero__amount ${
              (totalCash || 0) < 0 ? "wallet-hero__amount--negative" : ""
            }`}
          >
            Rp {FormatNumber(Math.round(totalCash || 0))}
          </div>
          <div className="wallet-hero__sub">
            {cash && cash.length === 0
              ? "Belum ada catatan"
              : `${cash.length} catatan kas`}
          </div>
        </section>

        {/* ── Search ──────────────────────────────────────────── */}
        {cash && cash.length > 0 && (
          <div className="wallet-search">
            <span className="wallet-search__icon">
              <IoIosSearch />
            </span>
            <input
              type="search"
              className="wallet-search__input"
              placeholder="Cari catatan kas…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                type="button"
                className="wallet-search__clear"
                onClick={() => setSearch("")}
                aria-label="Hapus pencarian"
              >
                <IoIosClose />
              </button>
            )}
          </div>
        )}

        {/* ── List / empty / no-match ─────────────────────────── */}
        {cash && cash.length === 0 ? (
          <div className="wallet-empty">
            <IoIosCash className="wallet-empty__icon" />
            <div className="wallet-empty__title">Belum ada catatan kas</div>
            <div className="wallet-empty__desc">
              Hitung uang fisik di dompet, brankas, atau celengan. Catat di sini
              supaya total bisa dibandingkan dengan saldo catatan — kalau beda,
              berarti ada transaksi yang lupa dicatat.
            </div>
            <button
              type="button"
              className="wallet-empty__cta"
              onClick={addCash}
            >
              Tambah catatan pertama →
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="wallet-placeholder">
            Tidak ada catatan yang cocok dengan "{search}".
          </div>
        ) : (
          <ul className="wallet-list">
            {filtered.map((d) => {
              const isNegative = (d.amount || 0) < 0;
              const isZero = (d.amount || 0) === 0;
              return (
                <li
                  key={d.id}
                  className={`wallet-card ${
                    isNegative ? "wallet-card--negative" : ""
                  }`}
                  onClick={() => editCash(d)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) =>
                    (e.key === "Enter" || e.key === " ") && editCash(d)
                  }
                >
                  <span className="wallet-card__rail" aria-hidden />
                  <span className="wallet-card__icon">
                    <IoIosCash />
                  </span>
                  <div className="wallet-card__body">
                    <div className="wallet-card__name">{d.name}</div>
                  </div>
                  <div className="wallet-card__right">
                    <div
                      className={`wallet-card__amount ${
                        isZero ? "wallet-card__amount--zero" : ""
                      }`}
                    >
                      {isNegative ? "−" : ""}Rp{" "}
                      {FormatNumber(Math.abs(Math.round(d.amount)))}
                    </div>
                    <IoIosArrowForward className="wallet-card__chev" />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <Fab icon={IoMdAdd} onClick={addCash} />
    </>
  );
}
